import {TextField} from "@mui/material";
import {useEffect, useState} from "react";
import {
    DialogActions,
    DialogContent,
    MultipleLineError,
    FormattedFormRow,
    FormContainer,
    FormDialogActionButtons,
    NumberField,
} from "@dvrt/graphql-widgets";
import CreateAnotherCheckBox from "../../CreateAnotherCheckBox";
import {useForm} from "@/utils/Form";
import {useCreateCallGroupMutation} from "../queries.generated";
import ErrorHelper from "../../../utils/ErrorHelper";
import {showNotification} from "@/actions";
import {Link} from "../../widgets";
import {CreateCallGroupMutation, CreateCallGroupMutationVariables} from "../queries.generated";
import {CallGroupType} from "@/api";
import EndpointCreateDialog, {
    domainAllocationToQuotaInfo,
    EndpointLimitInfo,
    QuotaInfo,
} from "@/components/EndpointCreateDialog";
import {useCallQueueLimitLazyQuery} from "./queries.generated";
import {defineMessage, MessageDescriptor} from "react-intl";

type ContentFormProps = {
    domainId: number;
    onSuccess: () => void;
    close: () => void;
    successItemPath: string;
    quotaInfo?: QuotaInfo;
};

export type Props = {
    isOpen: boolean;
    title: MessageDescriptor;
} & ContentFormProps;

const DialogForm = ({domainId, onSuccess, close, quotaInfo, successItemPath}: ContentFormProps) => {
    const form = useForm<CreateCallGroupMutation, CreateCallGroupMutationVariables>(
        useCreateCallGroupMutation,
        "createCallGroup",
        {
            domainId,
            name: "",
            type: CallGroupType.CallQueue,
            shortNumber: "",
            sequentialTimeout: 10,
            pickup: false,
        },
        (_form) =>
            showNotification(
                defineMessage({
                    id: "call_group_created",
                    defaultMessage: "Call group {link} has been successfully created.",
                }),
                "success",
                {
                    link: (
                        <Link
                            to={`/domains/${domainId}/${successItemPath}/${_form.result.data.createCallGroup.id}/settings`}
                        >
                            {_form.values.name}
                        </Link>
                    ),
                },
                10000,
            ),
    );
    const errorHelper = new ErrorHelper(form.errors);
    const [createAnother, setCreateAnother] = useState(false);

    function handleClose() {
        close();
        form.reset();
    }

    async function submit() {
        if (!(await form.submit())) return;
        onSuccess();

        if (createAnother) form.reset();
        else handleClose();
    }

    return (
        <>
            <DialogContent>
                <FormContainer>
                    <EndpointLimitInfo quotaInfo={quotaInfo} errors={errorHelper.fieldErrors("limit")} />
                    <FormattedFormRow label={defineMessage({id: "name", defaultMessage: "Name"})} required>
                        <TextField
                            variant="standard"
                            margin="dense"
                            value={form.values.name}
                            onChange={(event) => form.updateValues({name: event.target.value})}
                            error={errorHelper.hasError("name")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("name")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "identifier", defaultMessage: "Identifier"})} required>
                        <TextField
                            variant="standard"
                            margin="dense"
                            value={form.values.shortNumber}
                            onChange={(event) => form.updateValues({shortNumber: event.target.value})}
                            error={errorHelper.hasError("shortNumber")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("shortNumber")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "sequentialTimeout", defaultMessage: "Ringing timeout (seconds)"})}
                        required
                    >
                        <NumberField
                            // fullWidth
                            variant="standard"
                            margin="dense"
                            value={form.values.sequentialTimeout}
                            max={90}
                            min={0}
                            update={(newValue: number) => {
                                form.updateValues({sequentialTimeout: newValue});
                            }}
                            errors={errorHelper.fieldErrors("sequentialTimeout")}
                        />
                    </FormattedFormRow>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <CreateAnotherCheckBox state={createAnother} setState={setCreateAnother} />
                <FormDialogActionButtons
                    mainAction={submit}
                    secondaryAction={() => form.reset()}
                    cancel={close}
                    enabled={form.hasChanged}
                />
            </DialogActions>
        </>
    );
};

const CreateCallGroupDialog = ({domainId, onSuccess, isOpen, close, title, successItemPath}: Props) => {
    const [quotaInfo, setQuotaInfo] = useState<QuotaInfo | undefined>();
    const [limitInfoQuery] = useCallQueueLimitLazyQuery({
        fetchPolicy: "no-cache",
        variables: {domainId},
    });

    useEffect(() => {
        if (isOpen) {
            limitInfoQuery().then((result) => {
                if (result.error) {
                    // TODO: Show error message
                    console.error(result.error);
                    return;
                }

                setQuotaInfo(domainAllocationToQuotaInfo(result.data.domainAllocation.services.ccQueue));
            });
        } else {
            setQuotaInfo(undefined);
        }
    }, [isOpen, limitInfoQuery]);

    return (
        <EndpointCreateDialog
            title={title}
            isOpen={isOpen}
            closeDialog={close}
            quotaInfo={quotaInfo}
            loading={quotaInfo === undefined}
        >
            <DialogForm
                domainId={domainId}
                onSuccess={onSuccess}
                close={close}
                quotaInfo={quotaInfo}
                successItemPath={successItemPath}
            />
        </EndpointCreateDialog>
    );
};

export default CreateCallGroupDialog;
